import React, { useEffect, useState } from "react";
import "./countryRates.scss";
import { ApiGetWallet, ApiPostWallet } from "../../helpers/API/ApiData";
import Success from "../Toaster/Success/Success";
import Loader from "../common/Loader";
import CloseIcon from "../../assets/icons/new-close.svg";
import SearchIcon from "../../assets/icons/small-search.svg";

export default function CountryRates() {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [countryRates, setCountryRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    error: false,
  });

  const fetchCountryRates = async () => {
    setLoading(true);
    try {
      const response = await ApiGetWallet("countryRate");
      if (response?.data?.status === 200 && response?.data?.data) {
        setCountryRates(response.data.data);
        setFilteredRates(response.data.data);
      }
    } catch (error) {
      console.log("Error fetching country rates:", error);
      setToast({
        show: true,
        message: "Failed to load country rates",
        error: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await ApiPostWallet("countryRate/trigger-update");
      if (response?.data?.status === 200) {
        setToast({
          show: true,
          message: "Country rates refreshed successfully",
          error: false,
        });
        // Reload the table data
        await fetchCountryRates();
      } else {
        throw new Error("Refresh failed");
      }
    } catch (error) {
      console.log("Error refreshing country rates:", error);
      setToast({
        show: true,
        message: "Failed to refresh country rates",
        error: true,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    fetchCountryRates();
  }, []);

  // Filter countries based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredRates(countryRates);
    } else {
      const filtered = countryRates.filter((rate) =>
        (rate.country && rate.country.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rate.code && rate.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rate.currency && rate.currency.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredRates(filtered);
    }
  }, [searchTerm, countryRates]);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", error: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="country-rates-wrapper">
      <div className="full-page-header-alignment">
        <div className="header-title">
          <div className="close-icon" onClick={() => window.history.back()}>
            <img src={CloseIcon} alt="Close" />
          </div>
          <h2>Country Rates</h2>
        </div>
        <div className="blue-button">
          <button onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing..." : "Refresh Rates"}
          </button>
        </div>
      </div>

      <div className="full-page-body-top-alignment">
        <div className="all-content-alignment">
          <div className="country-rates-data-width">
            <div className="sub-header-alignment">
              <div className="total-count">
                <p>Total number of country - {filteredRates.length}</p>
              </div>
              <div className="search-relative">
                <input
                  type="text"
                  placeholder="Search by country, code, or currency..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="search-icon-alignment">
                  <img src={SearchIcon} alt="SearchIcon" />
                </div>
              </div>
            </div>

            <div className="country-rates-table-show">
              <table>
                <thead>
                  <tr>
                    <th align="left">Country</th>
                    <th align="left">Code</th>
                    <th align="left">Currency</th>
                    <th align="left">Multiplier from INR</th>
                    <th align="left">Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {countryRates.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data">
                        No country rates available
                      </td>
                    </tr>
                  ) : (
                    filteredRates.map((rate, index) => (
                      <tr key={index}>
                        <td>{rate.country || "N/A"}</td>
                        <td>{rate.countryCode || "N/A"}</td>
                        <td>{rate.currency || "N/A"}</td>
                        <td>{rate.multiplier_from_inr || "N/A"}</td>
                        <td>{formatDate(rate.last_updated)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {toast.show && (
        <Success
          modal={toast.show}
          toastmsg={toast.message}
          er={toast.error}
        />
      )}
    </div>
  );
}